import "./index.css";

import { process } from "@tql/ui-components-react/kendo/data-query";
import type { GridColumnProps } from "@tql/ui-components-react/kendo/grid";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { ShipmentNotificationModal } from "~/features/notifications/components/shipment-notification-modal/shipment-notification-modal.tsx";
import { DocumentViewer } from "~/shared/components/document-viewer/document-viewer.tsx";
import { useDocumentViewer } from "~/shared/components/document-viewer-flyout/hooks/use-document-viewer.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useGridFilterTabNavigation } from "~/shared/hooks/use-grid-filter-tab-navigation.ts";
import { useInfiniteScroll } from "~/shared/hooks/use-infinite-scroll.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useSharedUserFilter } from "~/shared/hooks/use-shared-user-filter.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";
import { getDefaultShipmentBoardGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import {
	collectActiveFilterFields,
	extractColumnFilters,
} from "~/shared/utils/extract-column-filters.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useGetInternalUserPreferences } from "../profile/hooks/internal/use-get-internal-user-preferences.ts";
import { useInternalGridPreferencesSync } from "../profile/hooks/internal/use-internal-grid-preferences-sync.ts";
import { useGetUserPreferences } from "../profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "../profile/hooks/use-grid-preferences-sync.ts";
import { ExternalShipmentBoardHeader } from "./components/external-shipment-board/external-shipment-board-header.tsx";
import { InternalShipmentBoardHeader } from "./components/internal-shipment-board/internal-shipment-board-header.tsx";
import { LoadDetailsDrawer } from "./components/load-details-drawer/load-details-drawer.tsx";
import { MobileShipmentBoard } from "./components/mobile-shipment-board/mobile-shipment-board.tsx";
import {
	createSkeletonAwareRow,
	SKELETON_ROWS,
} from "./components/skeleton-aware-row.tsx";
import {
	GRID_CLASS_EXTERNAL,
	GRID_CLASS_INTERNAL,
} from "./constants/class-names.ts";
import { internalUserFilters } from "./constants/internal-user-filters.ts";
import { getShipmentDateRangeCutoff } from "./constants/shipment-date-filter.ts";
import { useCustomerSelection } from "./hooks/use-customer-selection.ts";
import { useExternalPaging } from "./hooks/use-external-paging.ts";
import { useLoadColumns } from "./hooks/use-load-columns.tsx";
import { useLoadDocumentGeneration } from "./hooks/use-load-document-generation.tsx";
import { useLoadsData } from "./hooks/use-loads-data-v2.ts";
import { useShipmentGridState } from "./hooks/use-shipment-grid-state.ts";
import { shipmentDateRangeAtom } from "./state/shipment-date-filter-atom.ts";
import { compareDefaultLoadOrder } from "./utils/compare-default-load-order.ts";
import { filterLoadsByPickupDate } from "./utils/filter-loads-by-pickup-date.ts";
import { loadMatchesSearchAll } from "./utils/get-load-board-search-values.ts";
import { LoadCellContext } from "./utils/load-cell-context.tsx";

const GRID_STYLE_FILL_HEIGHT = { height: "100%" } as const;

export const Loads = () => {
	const { isInternal } = useDeploymentType();
	const { flagValue } = useFeatureFlagGate("traxLtlShipmentBoardPageSize");
	const isMobile = useIsMobile();

	const {
		selectedCustomers,
		externalCustomers,
		handleChange,
		handleFilterChange,
	} = useCustomerSelection({ isInternal });

	const [notificationLoad, setNotificationLoad] = useState<Load | null>(null);
	const [searchAllValue, setSearchAllValue] = useState("");

	const handleSearchAllChange = useCallback((value: string) => {
		setSearchAllValue(value);
	}, []);

	const handleSubscribeToNotifications = useCallback((load: Load) => {
		setNotificationLoad(load);
	}, []);

	const {
		gridFilter,
		gridSort,
		shipmentFilter,
		activeCardType,
		ltlBrokerIds,
		primaryBrokerIds,
		handleGridFilterChange,
		handleGridSortChange,
		onClickSummaryCard,
		resetBoardFilters,
		setAcknowledgmentFilter,
	} = useShipmentGridState();

	// Seed shared filter atom from URL/sessionStorage-restored values so deep
	// links keep working. Falls back to the default employee seed inside the
	// hook when none of these have data.
	const initialSharedFilterSeed = useCallback(() => {
		if (ltlBrokerIds.length > 0) {
			return {
				paramType: "ltlBrokers" as const,
				value: ltlBrokerIds.map((id) => ({ id, name: String(id) })),
			};
		}
		if (primaryBrokerIds.length > 0) {
			return {
				paramType: "laes" as const,
				value: primaryBrokerIds.map((id) => ({ id, name: String(id) })),
			};
		}
		if (selectedCustomers.length > 0) {
			return {
				paramType: "customers" as const,
				value: selectedCustomers.map((c) => ({ id: c.id, name: c.name })),
			};
		}
		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot seed; captured values represent first-mount state
	}, []);

	const {
		selectedFilter: selectedUserFilter,
		value: userFilterValue,
		ltlBrokerIds: atomLtlBrokerIds,
		primaryBrokerIds: atomPrimaryBrokerIds,
		selectedCustomers: atomSelectedCustomers,
		handleChange: handleUserFilterChange,
		handleSelectFilter: handleSelectUserFilter,
	} = useSharedUserFilter(internalUserFilters, {
		enabled: isInternal,
		initialSeed: initialSharedFilterSeed,
	});

	const [debouncedGridFilter] = useDebounceValue(gridFilter, 300);
	const columnFilters = useMemo(
		() => (isInternal ? extractColumnFilters(debouncedGridFilter) : undefined),
		[isInternal, debouncedGridFilter],
	);

	const shipmentDateRange = useAtomValue(shipmentDateRangeAtom);

	const lookbackDays = useMemo(() => {
		if (isInternal) {
			return undefined;
		}
		// Reuse a single "now" so the cutoff and the diff calculation can't drift
		// apart by the few milliseconds between two separate `new Date()` calls
		// (which previously bumped whole-day boundaries up by one via ceil).
		const now = new Date();
		const dateInPast = getShipmentDateRangeCutoff(shipmentDateRange, now);
		const diffTime = Math.abs(now.getTime() - dateInPast.getTime());
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	}, [isInternal, shipmentDateRange]);

	const {
		loadsData: { loads, summary },
		isLoading: isLoadingLoads,
		isFetchingLoads,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
		refetch,
	} = useLoadsData({
		selectedCustomers: isInternal ? atomSelectedCustomers : selectedCustomers,
		shipmentFilter,
		ltlBrokerIds: isInternal ? atomLtlBrokerIds : ltlBrokerIds,
		primaryBrokerIds: isInternal ? atomPrimaryBrokerIds : primaryBrokerIds,
		pageSize: flagValue as number,
		columnFilters,
		lookbackDays,
	});

	const { data: externalUserPreferences } = useGetUserPreferences(
		GridIdentifiers["Shipment Board"],
	);
	const { data: internalUserPreferences } = useGetInternalUserPreferences(
		GridIdentifiers["Shipment Board"],
	);
	// Internal and external preferences come from independent endpoints/hooks;
	// select the active set for this deployment without coupling the two flows.
	const userPreferences = isInternal
		? internalUserPreferences
		: externalUserPreferences;

	// The pickup-date timeframe filter is an external-board feature. Internal
	// mode is excluded because its summary cards come from the unfiltered
	// backend summary, so client-side date filtering would present conflicting
	// totals between the cards and the grid.
	const dateFilteredLoads = useMemo(
		() =>
			isInternal ? loads : filterLoadsByPickupDate(loads, shipmentDateRange),
		[isInternal, loads, shipmentDateRange],
	);

	const sortedLoads = useMemo(
		() => [...dateFilteredLoads].sort(compareDefaultLoadOrder),
		[dateFilteredLoads],
	);

	const [debouncedSearchAll] = useDebounceValue(searchAllValue, 300);

	const processedInternalLoads = useMemo(() => {
		if (!isInternal) return sortedLoads;
		// In internal mode the backend already applies any column filter whose
		// value made it through `extractColumnFilters` (≥3 chars for strings).
		// Skip the client-side filter pass when every active field is
		// represented in `columnFilters`; fall back to client filtering only
		// for the interim 1-2 char window where the server hasn't yet seen
		// the value. This avoids re-iterating the entire accumulated dataset
		// on every keystroke after the steady-state is reached.
		const activeFields = collectActiveFilterFields(gridFilter);
		const allFieldsServerFiltered =
			activeFields.size === 0 ||
			(columnFilters !== undefined &&
				[...activeFields].every((field) =>
					Object.hasOwn(columnFilters, field),
				));
		const result = process(sortedLoads, {
			filter: allFieldsServerFiltered ? undefined : gridFilter,
			sort: gridSort,
		});
		return result.data as Load[];
	}, [isInternal, sortedLoads, gridFilter, gridSort, columnFilters]);

	const handleRefresh = useCallback(() => {
		refetch.combined();
	}, [refetch]);
	const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
	const [selectedLoad, setSelectedLoad] = useState<Load | null>(null);

	const { viewerState, openViewer, closeViewer, selectDocument } =
		useDocumentViewer();
	const [documentLoad, setDocumentLoad] = useState<Load | null>(null);
	const documentPoNumber = documentLoad?.poNumber ?? undefined;
	const {
		headerAction: generateDocumentsHeaderAction,
		modal: generateDocumentsModal,
	} = useLoadDocumentGeneration({ load: documentLoad, isInternal });

	const handleDocumentClick = useCallback(
		(load: Load) => {
			setDocumentLoad(load);
			openViewer();
		},
		[openViewer],
	);

	const handleOpenDetailsDrawer = useCallback((load: Load) => {
		setSelectedLoad(load);
		setIsDetailsDrawerOpen(true);
	}, []);

	const handleCloseDetailsDrawer = useCallback(() => {
		setIsDetailsDrawerOpen(false);
	}, []);

	const { allColumns, visibleColumns, cellContextValue } = useLoadColumns({
		isLoading: isLoadingLoads,
		isInternal,
		onSubscribeToNotifications: handleSubscribeToNotifications,
		onDetailsClick: handleOpenDetailsDrawer,
		onDocumentClick: handleDocumentClick,
		columnPrefs: userPreferences?.columns,
	});

	const searchFilteredLoads = useMemo(() => {
		if (!debouncedSearchAll.trim()) return sortedLoads;

		const term = debouncedSearchAll.trim();
		return sortedLoads.filter((load) =>
			loadMatchesSearchAll(load, allColumns, term),
		);
	}, [sortedLoads, debouncedSearchAll, allColumns]);

	const { skip, take, handlePageChange, isLoadingPreference } =
		useExternalPaging({
			enabled: !isInternal,
			data: searchFilteredLoads,
		});

	const isGridLoading =
		isLoadingLoads ||
		(isInternal && isFetchingLoads && !isFetchingNextPage) ||
		(!isInternal && isLoadingPreference);

	const gridPrefsOptions = {
		defaultPrefs:
			getDefaultShipmentBoardGridPrefs(isInternal)[
				GridIdentifiers["Shipment Board"]
			]?.columns,
	};
	// Both hooks are called unconditionally (rules of hooks); each self-disables
	// its network request for the other deployment, so only the selected one is
	// active. The external sync flow is left fully untouched here.
	const externalGridPrefs = useGridPreferencesSync(
		GridIdentifiers["Shipment Board"],
		gridPrefsOptions,
	);
	const internalGridPrefs = useInternalGridPreferencesSync(
		GridIdentifiers["Shipment Board"],
		gridPrefsOptions,
	);
	const {
		gridKey,
		onColumnVisibilityChange: handleToggleColumn,
		onColumnReorder: handleColumnReorder,
		onColumnResize: handleColumnResize,
		resetColumns: handleResetColumns,
	} = isInternal ? internalGridPrefs : externalGridPrefs;

	// Fix tab order for filter inputs to follow left-to-right column order
	useGridFilterTabNavigation();

	const internalGridData = useMemo(() => {
		if (!isInternal) return processedInternalLoads;
		if (!isFetchingNextPage) return processedInternalLoads;
		return [...processedInternalLoads, ...SKELETON_ROWS];
	}, [isInternal, isFetchingNextPage, processedInternalLoads]);

	const { handleScroll, resetScrollPosition } = useInfiniteScroll({
		enabled: isInternal,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		data: internalGridData,
		fallbackContainerSelector: ".loads-grid-container .k-grid-content",
	});

	useEffect(() => {
		resetScrollPosition();
	}, [gridFilter, gridSort, resetScrollPosition]);

	const visibleColumnCount = visibleColumns.length;
	const SkeletonAwareRow = useMemo(
		() => createSkeletonAwareRow(visibleColumnCount),
		[visibleColumnCount],
	);

	const internalRowsProp = useMemo(
		() => ({ data: SkeletonAwareRow }),
		[SkeletonAwareRow],
	);

	const gridPaginationProps = useMemo(
		() =>
			isInternal
				? {
						onScroll: handleScroll,
						pageable: false as const,
						scrollable: "scrollable" as const,
						autoProcessData: false,
						data: internalGridData,
						total: internalGridData.length,
						style: GRID_STYLE_FILL_HEIGHT,
						rows: internalRowsProp,
					}
				: {
						onPageChange: handlePageChange,
						skip,
						take,
						style: GRID_STYLE_FILL_HEIGHT,
					},
		[
			isInternal,
			handleScroll,
			internalGridData,
			internalRowsProp,
			handlePageChange,
			skip,
			take,
		],
	);

	const gridClassName = isInternal ? GRID_CLASS_INTERNAL : GRID_CLASS_EXTERNAL;

	return (
		<TraxPageLayout>
			<TraxPageHeader
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Shipment Board"}
			/>
			<>
				{!isInternal && isMobile ? (
					<>
						<ExternalShipmentBoardHeader
							allColumns={allColumns}
							externalCustomers={externalCustomers}
							onChangeColumn={handleToggleColumn}
							onClearFilters={resetBoardFilters}
							onCustomerChange={handleChange}
							onCustomerFilterChange={handleFilterChange}
							onRefresh={handleRefresh}
							onResetColumns={handleResetColumns}
							onSearchAllChange={handleSearchAllChange}
							searchAllValue={searchAllValue}
							selectedCustomers={selectedCustomers}
						/>
						<MobileShipmentBoard
							detailsFlyoutEnabled={cellContextValue.detailsFlyoutEnabled}
							loading={isGridLoading}
							onDetailsClick={handleOpenDetailsDrawer}
							onDocumentClick={handleDocumentClick}
							onShipmentNotificationsClick={handleSubscribeToNotifications}
							shipmentData={searchFilteredLoads}
						/>
					</>
				) : (
					<div className={"track-container"}>
						{isInternal ? (
							<InternalShipmentBoardHeader
								activeCardType={activeCardType}
								allColumns={allColumns}
								isLoadingLoads={isLoadingLoads}
								needsAcknowledgment={shipmentFilter.needsAcknowledgment}
								onAcknowledgmentFilterChange={setAcknowledgmentFilter}
								onChangeColumn={handleToggleColumn}
								onClearFilters={resetBoardFilters}
								onClickSummaryCard={onClickSummaryCard}
								onRefresh={handleRefresh}
								onResetColumns={handleResetColumns}
								onSelectUserFilter={handleSelectUserFilter}
								onUserFilterChange={handleUserFilterChange}
								selectedUserFilter={selectedUserFilter}
								summary={summary}
								userFilterValue={userFilterValue}
								userFilters={internalUserFilters}
							/>
						) : (
							<ExternalShipmentBoardHeader
								allColumns={allColumns}
								externalCustomers={externalCustomers}
								onChangeColumn={handleToggleColumn}
								onClearFilters={resetBoardFilters}
								onCustomerChange={handleChange}
								onCustomerFilterChange={handleFilterChange}
								onRefresh={handleRefresh}
								onResetColumns={handleResetColumns}
								onSearchAllChange={handleSearchAllChange}
								searchAllValue={searchAllValue}
								selectedCustomers={selectedCustomers}
							/>
						)}

						<div
							className={"loads-grid-container"}
							data-testid={"shipment-board-grid"}
						>
							<LoadCellContext.Provider value={cellContextValue}>
								<TraxGrid
									key={gridKey}
									className={gridClassName}
									columns={visibleColumns as GridColumnProps[]}
									data={searchFilteredLoads}
									editable={false}
									filter={gridFilter}
									filterable={true}
									loading={isGridLoading}
									onColumnReorder={handleColumnReorder}
									onColumnResize={handleColumnResize}
									onFilterChange={handleGridFilterChange}
									onSortChange={handleGridSortChange}
									reorderable={true}
									resizable={true}
									sort={gridSort}
									{...gridPaginationProps}
								/>
							</LoadCellContext.Provider>
						</div>
					</div>
				)}
				<ShipmentNotificationModal
					customerId={notificationLoad?.customerId ?? 0}
					isOpen={notificationLoad !== null}
					onClose={() => {
						setNotificationLoad(null);
					}}
					poNumber={notificationLoad?.poNumber ?? 0}
				/>
				<LoadDetailsDrawer
					isOpen={isDetailsDrawerOpen}
					load={selectedLoad}
					onClose={handleCloseDetailsDrawer}
				/>
				<DocumentViewer
					extraHeaderActions={generateDocumentsHeaderAction}
					onClose={closeViewer}
					onSelectDocument={selectDocument}
					poNumber={documentPoNumber}
					viewerState={viewerState}
				/>
				{generateDocumentsModal}
			</>
		</TraxPageLayout>
	);
};
